/**
 * useRouteGuards - Compose and execute multiple guards
 *
 * Core pattern for handling multiple guards per route
 * Mirrors legacy app's canActivate: [Guard1, Guard2, Guard3]
 *
 * Guards execute in order:
 * auth -> role -> vendor -> provider -> display
 *
 * Stops on first failure and returns redirect
 */

import { useLocation } from 'react-router-dom';
import { GuardResult, GuardType, RouteConfig } from '../types';
import { useAuth } from '../../auth';
import { AuthState, checkAuthGuard } from '../guards/useAuthGuard';
import { checkRoleGuard, RoleAuthState } from '../guards/useRoleGuard';
import { checkVendorGuard } from '../guards/useVendorGuard';
import { checkProviderViewGuard } from '../guards/useProviderViewGuard';
import { checkDisplayGuard } from '../guards/useDisplayGuard';
import { environment } from '@web/config';

/**
 * Main guard composition hook
 *
 * Calls useAuth() once at the top level (React rules-of-hooks compliant),
 * then passes auth state to pure guard functions.
 *
 * Usage:
 *   const result = useRouteGuards(['auth', 'vendor', 'role'], routeConfig);
 *   if (!result.allowed) navigate(result.redirect);
 */
export function useRouteGuards(guards: GuardType[], route: RouteConfig): GuardResult {
  const auth = useAuth();
  const location = useLocation();

  if (!guards || guards.length === 0) {
    return { allowed: true };
  }

  // Build auth state once for all guards
  const authState = {
    authenticated: auth.authenticated,
    loading: auth.loading,
    user: auth.user,
    config: auth.config,
  };

  const roleState = {
    authenticated: auth.authenticated,
    hasRole: auth.hasRole,
    user: auth.user,
    config: auth.config,
  };

  // Execute guards in order - first failure stops chain
  for (const guardType of guards) {
    let result: GuardResult;

    switch (guardType) {
      case 'auth':
        result = checkAuthGuard(route, authState);
        break;

      case 'role':
        result = checkRoleGuard(route, roleState);
        break;

      case 'vendor':
        result = checkVendorGuard(route, auth.user, location.pathname);
        break;

      case 'provider':
        result = checkProviderViewGuard(
          location.pathname,
          environment.tinLevelExternalChange,
          auth.isInternal
        );
        break;

      case 'display':
        // Display guard is async - skip in sync execution.
        // Must be handled separately with useEffect in the component.
        result = { allowed: true };
        break;

      default:
        result = { allowed: true };
    }

    if (!result.allowed) {
      return result;
    }
  }

  return { allowed: true };
}

/**
 * Async version for routes that need the display guard.
 * Call inside a useEffect.
 */
export async function checkRouteGuardsAsync(
  guards: GuardType[],
  route: RouteConfig,
  authState: AuthState,
  roleState: RoleAuthState,
  pathname: string
): Promise<GuardResult> {
  if (!guards || guards.length === 0) {
    return { allowed: true };
  }

  for (const guardType of guards) {
    let result: GuardResult;

    switch (guardType) {
      case 'auth':
        result = checkAuthGuard(route, authState);
        break;
      case 'role':
        result = checkRoleGuard(route, roleState);
        break;
      case 'vendor':
        result = checkVendorGuard(route, authState.user, pathname);
        break;
      case 'provider':
        result = checkProviderViewGuard(
          pathname,
          environment.tinLevelExternalChange,
          authState.config?.isInternal
        );
        break;
      case 'display':
        result = await checkDisplayGuard();
        break;
      default:
        result = { allowed: true };
    }

    if (!result.allowed) {
      return result;
    }
  }

  return { allowed: true };
}
